import { useState, useEffect, useRef } from "react";
import {
  BadgeCheck,
  BadgeIndianRupee,
  ChevronDown,
  Eye,
  EyeOff,
  HandCoins,
  LockKeyhole,
  Send,
  ShieldCheck,
  UserRound,
  WalletCards,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import {
  getFundDropdownValues,
  transferFunds,
  clearTransferState,
  lookupReceiver,
} from "../../../Redux/Fund-Transfer-api/slice";
import { KycStatus } from "../../../Redux/Fund-Transfer-api/types";
import "./UserFundTransferPage.css";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);

const FundTransfer = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.customerAuth);
  const {
    dropdownValues,
    loading,
    error,
    success,
    receiverInfo,
    receiverLoading,
    receiverError,
  } = useAppSelector((state) => state.transfer);

  const [idError, setIdError] = useState("");

  const [formData, setFormData] = useState({
    receiverUserId: "",
    amount: "",
    component: "", // WalletComponent
  });

  const [txnPassword, setTxnPassword] = useState("");
  const [txnPasswordError, setTxnPasswordError] = useState("");
  const [showTxnPassword, setShowTxnPassword] = useState(false);

  const [selectedIncentive, setSelectedIncentive] = useState<any>(null);
  const [incentiveDropdownOpen, setIncentiveDropdownOpen] = useState(false);
  const incentiveDropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dispatch(getFundDropdownValues());

    return () => {
      dispatch(clearTransferState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      alert("Fund transfer successful!");
      setFormData({ receiverUserId: "", amount: "", component: "" });
      setTxnPassword("");
      setTxnPasswordError("");
      setSelectedIncentive(null);
      dispatch(getFundDropdownValues()); // Refresh balances
    }
  }, [success, dispatch]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (
        incentiveDropdownRef.current &&
        !incentiveDropdownRef.current.contains(event.target as Node)
      ) {
        setIncentiveDropdownOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIncentiveDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const amountValue = Number(formData.amount) || 0;
  const totalDebit = amountValue;

  const handleCustomerIdChange = (value: string) => {
    const upper = value.toUpperCase();
    if (upper.length > 11) return;
    setFormData((prev) => ({ ...prev, receiverUserId: upper }));

    if (!upper) {
      setIdError("");
      return;
    }
    if (!upper.startsWith("S")) {
      setIdError("Customer ID must start with 'S'");
      return;
    }
    if (upper.length < 8) {
      setIdError("Customer ID must be at least 8 characters");
      return;
    }
    setIdError("");
    dispatch(lookupReceiver(upper));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleIncentiveSelect = (val: any) => {
    setSelectedIncentive(val);
    setFormData((prev) => ({ ...prev, component: val.component }));
    setIncentiveDropdownOpen(false);
  };

  const validateTxnPassword = (val: string): string => {
    if (!val) return "Transaction password is required";
    if (val.length > 15)
      return "Transaction password must not exceed 15 characters";
    if (!/[A-Z]/.test(val)) return "Must contain at least one uppercase letter";
    if (!/[a-z]/.test(val)) return "Must contain at least one lowercase letter";
    if (!/[0-9]/.test(val)) return "Must contain at least one number";
    if (!/[^A-Za-z0-9]/.test(val))
      return "Must contain at least one special character";
    return "";
  };

  const handleTxnPasswordChange = (val: string) => {
    if (val.length > 15) return;
    setTxnPassword(val);
    if (txnPasswordError) setTxnPasswordError(validateTxnPassword(val));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const senderUserId = user?.registerId || user?._id;

    if (!senderUserId) {
      alert("Sender profile not found.");
      return;
    }

    if (!formData.receiverUserId || !formData.amount || !formData.component) {
      alert("Please fill in all required fields.");
      return;
    }

    if (!formData.receiverUserId.startsWith("S")) {
      setIdError("Customer ID must start with 'S'");
      return;
    }
    if (
      formData.receiverUserId.length < 8 ||
      formData.receiverUserId.length > 11
    ) {
      setIdError("Customer ID must be between 8 and 11 characters");
      return;
    }
    if (!receiverInfo) {
      setIdError("Please enter a valid Customer ID to fetch customer name");
      return;
    }

    const txnPwdError = validateTxnPassword(txnPassword);
    if (txnPwdError) {
      setTxnPasswordError(txnPwdError);
      return;
    }

    // Per requirements, sender KYC must be VERIFIED
    const senderKycStatus = KycStatus.VERIFIED;

    dispatch(
      transferFunds({
        senderUserId: senderUserId,
        senderKycStatus: senderKycStatus,
        senderName: user?.fullName || "",
        receiverUserId: formData.receiverUserId,
        receiverKycStatus: KycStatus.VERIFIED,
        receiverName: receiverInfo?.name || "",
        component: formData.component as any,
        amount: Number(formData.amount),
      }),
    );
  };

  return (
    <section className="fund-transfer-page">
      <div className="fund-transfer-shell">
        <header className="fund-transfer-header">
          <div className="fund-transfer-header-left">
            <div className="fund-transfer-header-icon">
              <WalletCards size={22} strokeWidth={2} />
            </div>
            <div>
              <span className="fund-transfer-eyebrow">MY WALLET</span>
              <h1>Fund Transfer</h1>
              <p>
                Transfer eligible wallet incentives securely to another Salar
                user.
              </p>
            </div>
          </div>
          <div className="fund-transfer-secure-pill">
            <ShieldCheck size={15} strokeWidth={2} />
            Secure transfer
          </div>
        </header>

        <div className="fund-transfer-layout">
          <form className="fund-transfer-card" onSubmit={handleSubmit}>
            <div className="fund-transfer-card-head">
              <div>
                <span>TRANSFER DETAILS</span>
                <h2>Send funds</h2>
                <p>
                  Enter the recipient and transfer information below.
                </p>
              </div>
              <div className="fund-transfer-card-mark">
                <Send size={18} strokeWidth={2} />
              </div>
            </div>

            {error && (
              <div className="fund-transfer-message" role="alert">
                {error}
              </div>
            )}

            <div className="fund-transfer-form-grid">
              <label className="fund-transfer-field fund-transfer-field-full">
                <span className="fund-transfer-label">Customer ID</span>
                <div className="fund-transfer-control">
                  <UserRound size={17} strokeWidth={2} />
                  <input
                    type="text"
                    name="receiverUserId"
                    value={formData.receiverUserId}
                    onChange={(e) => handleCustomerIdChange(e.target.value)}
                    maxLength={11}
                    placeholder="Enter Customer ID"
                    autoComplete="off"
                  />
                </div>
                {idError && (
                  <span className="fund-transfer-helper fund-transfer-helper-error">
                    {idError}
                  </span>
                )}
                {receiverLoading && (
                  <span className="fund-transfer-helper">
                    Looking up customer...
                  </span>
                )}
                {receiverError && !idError && (
                  <span className="fund-transfer-helper fund-transfer-helper-error">
                    {receiverError}
                  </span>
                )}
                {receiverInfo && (
                  <span className="fund-transfer-helper">
                    Customer: <strong>{receiverInfo.name}</strong>
                  </span>
                )}
              </label>

              <div
                className="fund-transfer-field fund-transfer-select-field fund-transfer-field-full"
                ref={incentiveDropdownRef}
              >
                <span className="fund-transfer-label">Incentive Wallet</span>
                <button
                  type="button"
                  className={`fund-transfer-select-trigger ${
                    incentiveDropdownOpen ? "is-open" : ""
                  }`}
                  onClick={() =>
                    setIncentiveDropdownOpen((current) => !current)
                  }
                  aria-haspopup="listbox"
                  aria-expanded={incentiveDropdownOpen}
                >
                  <HandCoins size={17} strokeWidth={2} />
                  <span
                    className={`fund-transfer-select-value ${
                      selectedIncentive ? "" : "is-placeholder"
                    }`}
                  >
                    {selectedIncentive
                      ? selectedIncentive.displayName.replace(
                          "Commission",
                          "Incentives",
                        )
                      : "Select Incentives"}
                  </span>
                  <ChevronDown
                    className="fund-transfer-select-arrow"
                    size={17}
                    strokeWidth={2}
                  />
                </button>

                {incentiveDropdownOpen && (
                  <div
                    className="fund-transfer-dropdown-card"
                    role="listbox"
                    aria-label="Select incentive wallet"
                  >
                    {dropdownValues.length > 0 ? (
                      dropdownValues.map((val: any, idx: number) => {
                        const isSelected =
                          selectedIncentive?.component === val.component;
                        return (
                          <button
                            key={idx}
                            type="button"
                            role="option"
                            aria-selected={isSelected}
                            className={`fund-transfer-dropdown-option ${
                              isSelected ? "is-selected" : ""
                            }`}
                            onClick={() => handleIncentiveSelect(val)}
                          >
                            <span className="fund-transfer-dropdown-option-copy">
                              <span className="fund-transfer-dropdown-option-label">
                                {val.displayName.replace(
                                  "Commission",
                                  "Incentives",
                                )}
                              </span>
                              <span className="fund-transfer-dropdown-option-balance">
                                Available: {formatCurrency(val.balance)}
                              </span>
                            </span>
                            {isSelected && (
                              <span className="fund-transfer-dropdown-option-check">
                                <BadgeCheck size={15} strokeWidth={2.2} />
                              </span>
                            )}
                          </button>
                        );
                      })
                    ) : (
                      <div className="fund-transfer-dropdown-empty">
                        No incentive wallets available.
                      </div>
                    )}
                  </div>
                )}

                {selectedIncentive && (
                  <span className="fund-transfer-helper">
                    Available balance:{" "}
                    <strong>{formatCurrency(selectedIncentive.balance)}</strong>
                  </span>
                )}
              </div>

              <label className="fund-transfer-field fund-transfer-field-full">
                <span className="fund-transfer-label">Transfer Amount</span>
                <div className="fund-transfer-control">
                  <BadgeIndianRupee size={17} strokeWidth={2} />
                  <input
                    type="number"
                    name="amount"
                    min="0"
                    step="0.01"
                    value={formData.amount}
                    onChange={handleInputChange}
                    placeholder="Enter Amount"
                    inputMode="decimal"
                  />
                </div>
              </label>

              <label className="fund-transfer-field fund-transfer-field-full">
                <span className="fund-transfer-label">
                  Transaction Password
                </span>
                <div className="fund-transfer-control">
                  <LockKeyhole size={17} strokeWidth={2} />
                  <input
                    type={showTxnPassword ? "text" : "password"}
                    value={txnPassword}
                    onChange={(e) => handleTxnPasswordChange(e.target.value)}
                    maxLength={15}
                    placeholder="Enter your transaction password"
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="fund-transfer-password-toggle"
                    onClick={() => setShowTxnPassword((current) => !current)}
                    aria-label={
                      showTxnPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showTxnPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                {txnPasswordError && (
                  <span className="fund-transfer-helper fund-transfer-helper-error">
                    {txnPasswordError}
                  </span>
                )}
              </label>
            </div>

            <div className="fund-transfer-summary">
              <div>
                <span>Transfer amount</span>
                <strong>{formatCurrency(amountValue)}</strong>
              </div>
              <div className="fund-transfer-summary-total">
                <span>Total debit</span>
                <strong>{formatCurrency(totalDebit)}</strong>
              </div>
            </div>

            <button
              type="submit"
              className="fund-transfer-submit"
              disabled={loading}
            >
              <Send size={17} strokeWidth={2} />
              {loading ? "Processing..." : "Submit Transfer"}
            </button>

            <div className="fund-transfer-footnote">
              <ShieldCheck size={14} strokeWidth={2} />
              Verify the recipient Customer ID and amount before submitting.
            </div>
          </form>

          <aside className="fund-transfer-side-card">
            <div className="fund-transfer-side-icon">
              <HandCoins size={22} strokeWidth={2} />
            </div>
            <span>TRANSFER GUIDE</span>
            <h3>Before you send</h3>
            <p>
              Choose the correct incentive wallet, confirm the recipient
              Customer ID and review the final debit amount.
            </p>
            <div className="fund-transfer-guide-list">
              <div>
                <strong>01</strong>
                <span>Enter recipient</span>
              </div>
              <div>
                <strong>02</strong>
                <span>Select incentive</span>
              </div>
              <div>
                <strong>03</strong>
                <span>Review & submit</span>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default FundTransfer;
